import React, { useMemo, useState } from 'react';
import { useRefillRequestModalContext } from '../../Web/RefillRequests/utils';
import { CreateMessageModal } from '../ContentModals/CreateMessageModal';
import { useDispatch } from 'react-redux';
import {
  CREATE_REFILL_REQUEST_MESSAGE_ERROR,
  createRefillRequestMessage,
  UPLOAD_REFILL_ATTACHMENT,
  uploadRefillAttachment,
} from '../../../redux/actions/refillRequestActions';
import { useQueryClient } from 'react-query';
import { cacheKeys } from '../../../hooks/refill-requests/cacheKeys';
import ResultModal from '../../../shared/components/Modals/MessageModal';

export function RefillRequestMessageModal() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const {
    isMessageModalOpen,
    setIsMessageModalOpen,
    refillRequestData,
    handleRefetchRefillRequest,
  } = useRefillRequestModalContext();

  const [isLoading, setIsLoading] = useState(false);
  const [responseState, setResponseState] = useState({
    show: false,
    type: '',
    message: '',
  });

  const contextSubheading = useMemo(() => {
    const medication = refillRequestData?.medication;
    const petName = refillRequestData?.petName;

    if (!medication || !petName) {
      return '';
    }

    return `Re: Refill Request for ${medication} for ${petName}`;
  }, [refillRequestData]);

  const handleSendMessage = async ({
    message,
    requiresResponse,
    attachments,
    onSuccess,
  }) => {
    if (
      !message &&
      attachments.images.length === 0 &&
      attachments.pdfs.length === 0
    ) {
      return;
    }

    setIsLoading(true);

    try {
      const uploadedAttachments = { images: [], pdfs: [] };

      for (const image of attachments.images) {
        const result = await dispatch(uploadRefillAttachment(image, 'images'));
        if (result.type === UPLOAD_REFILL_ATTACHMENT) {
          uploadedAttachments.images.push(result.payload.secure_url);
        }
      }

      for (const pdf of attachments.pdfs) {
        const result = await dispatch(uploadRefillAttachment(pdf, 'pdfs'));
        if (result.type === UPLOAD_REFILL_ATTACHMENT) {
          uploadedAttachments.pdfs.push(result.payload.secure_url);
        }
      }

      const messageResult = await dispatch(
        createRefillRequestMessage({
          prescriptionId: refillRequestData.id,
          message,
          requiresResponse,
          attachments: uploadedAttachments,
        }),
      );

      if (messageResult.type === CREATE_REFILL_REQUEST_MESSAGE_ERROR) {
        throw new Error('Failed to send message');
      }

      setResponseState({
        show: true,
        type: 'success',
        message: 'Message sent successfully!',
      });

      setIsMessageModalOpen(false);
      onSuccess?.();
      handleRefetchRefillRequest(false);

      setTimeout(() => {
        setResponseState({ show: false, type: '', message: '' });

        queryClient.invalidateQueries(
          cacheKeys.getRefillRequestMessages({
            refillRequestId: refillRequestData.id,
          }),
        );
      }, 2000);
    } catch (error) {
      console.error(error);

      setResponseState({
        show: true,
        type: 'danger',
        message: 'Failed to upload attachments or send message.',
      });

      setTimeout(() => {
        setResponseState({ show: false, type: '', message: '' });
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CreateMessageModal
        isOpen={isMessageModalOpen}
        setIsOpen={setIsMessageModalOpen}
        contextSubheading={contextSubheading}
        onSave={handleSendMessage}
        isLoading={isLoading}
      />

      <ResultModal
        show={responseState.show}
        type="alert"
        alertType={responseState.type}
        message={responseState.message}
        onClose={() => setResponseState({ show: false, type: '', message: '' })}
      />
    </>
  );
}
